import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';

const STATUS = {
  pending:     { label: 'Pending',     bg: '#b7860b' },
  assigned:    { label: 'Assigned',    bg: '#2471a3' },
  in_progress: { label: 'In Progress', bg: '#2471a3' },
  completed:   { label: 'Completed',   bg: '#1e8449' },
  cancelled:   { label: 'Cancelled',   bg: '#922b21' },
};

function fmtDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}
function fmtTime(t) {
  if (!t) return '';
  const [h, m] = t.split(':'); const hr = parseInt(h, 10);
  return `${hr % 12 || 12}:${m} ${hr < 12 ? 'AM' : 'PM'}`;
}
const fmtINR = n => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n);

const SoloHealerDashboard = () => {
  const { user, logout } = useAuth();
  const [bookings,   setBookings]   = useState([]);
  const [earnings,   setEarnings]   = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState('');
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [bRes, eRes] = await Promise.all([
          api.get('/solo/bookings'), api.get('/solo/earnings'),
        ]);
        setBookings(bRes.data.data || []);
        setEarnings(eRes.data.data || {});
      } catch { setError('Could not load your dashboard. Please refresh.'); }
      finally  { setLoading(false); }
    })();
  }, []);

  const today    = new Date().toISOString().slice(0, 10);
  const upcoming = bookings.filter(b => b.slotDate >= today && ['pending', 'assigned'].includes(b.status)).slice(0, 3);

  return (
    <div style={s.page}>
      <div style={s.header}>
        <span style={s.brand}>LifeChiAI</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <span style={s.welcomeText}>Welcome, {user?.firstName}</span>
          <button style={s.logoutBtn} onClick={logout}>Logout</button>
        </div>
      </div>

      <div style={s.body}>
        {error && <div style={s.errorBox}>{error}</div>}
        {loading ? (
          <div style={s.loadingBox}>Loading your dashboard…</div>
        ) : (<>
          {earnings && (
            <div style={s.earningsStrip}>
              {[
                { val: fmtINR(earnings.mtdEarned   ?? 0), label: 'THIS MONTH' },
                { val: fmtINR(earnings.totalEarned ?? 0), label: 'ALL-TIME'   },
                { val: earnings.sessionCount       ?? 0,  label: 'SESSIONS'   },
              ].map((stat, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <div style={s.earningDivider} />}
                  <div style={s.earningStat}>
                    <div style={s.earningNum}>{stat.val}</div>
                    <div style={s.earningLabel}>{stat.label}</div>
                  </div>
                </React.Fragment>
              ))}
            </div>
          )}

          <div style={s.section}>
            <div style={s.sectionLabel}>UPCOMING</div>
            {upcoming.length === 0 ? (
              <div style={s.emptyCard}>No upcoming bookings.</div>
            ) : upcoming.map(b => (
              <div key={b._id} style={s.upcomingCard}>
                <div style={s.upcomingPatient}>{b.forWhom}</div>
                <div style={s.upcomingMeta}>
                  {b.slotDate ? fmtDate(b.slotDate) : fmtDate(b.requestedAt)}
                  {b.slotTime    && ` · ${fmtTime(b.slotTime)}`}
                  {b.sessionType && ` · ${b.sessionType}`}
                </div>
                <span style={{ ...s.badge, background: STATUS[b.status]?.bg || '#555' }}>
                  {STATUS[b.status]?.label || b.status}
                </span>
              </div>
            ))}
          </div>

          <div style={s.section}>
            <div style={s.sectionLabel}>BOOKING HISTORY</div>
            {bookings.length === 0 ? (
              <div style={s.emptyCard}>No bookings yet.</div>
            ) : (
              <div style={s.historyList}>
                {bookings.map(b => {
                  const open = expandedId === b._id.toString();
                  const ref  = b._id.toString().slice(-8).toUpperCase();
                  return (
                    <div key={b._id} style={s.historyItem}>
                      <div style={s.historyRow}>
                        <div style={s.historyLeft}>
                          <div style={s.historyDate}>
                            {b.slotDate ? fmtDate(b.slotDate) : fmtDate(b.requestedAt)}
                          </div>
                          <div style={s.historyDetail}>
                            {[b.sessionType, b.forWhom].filter(Boolean).join(' · ') || 'Session'}
                          </div>
                          {b.condition && <div style={s.historyCondition}>{b.condition}</div>}
                          <button style={s.receiptToggle}
                            onClick={() => setExpandedId(open ? null : b._id.toString())}>
                            {open ? 'Hide Details' : 'View Details'}
                          </button>
                        </div>
                        <span style={{ ...s.badge, background: STATUS[b.status]?.bg || '#555' }}>
                          {STATUS[b.status]?.label || b.status}
                        </span>
                      </div>
                      {open && (
                        <div style={s.receiptPanel}>
                          <div style={s.receiptTitle}>BOOKING DETAILS</div>
                          {[
                            ['Reference', `#${ref}`],
                            ['Date',    b.slotDate ? fmtDate(b.slotDate) : fmtDate(b.requestedAt)],
                            ['Patient', b.forWhom  || '—'],
                            ['Service', b.sessionType || b.condition || 'Healing Session'],
                          ].map(([label, val]) => (
                            <div key={label} style={s.receiptRow}>
                              <span style={s.receiptLabel}>{label}</span>
                              <span style={s.receiptValue}>{val}</span>
                            </div>
                          ))}
                          <div style={s.receiptRow}>
                            <span style={s.receiptLabel}>Status</span>
                            <span style={{ ...s.badge, background: STATUS[b.status]?.bg || '#555' }}>
                              {STATUS[b.status]?.label || b.status}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>)}
        <div style={s.privacyFooter}>
          <a href="/privacy" style={s.privacyLink}>Privacy Policy</a>
        </div>
      </div>
    </div>
  );
};

const s = {
  page:             { minHeight: '100vh', background: 'linear-gradient(135deg, #071e12 0%, #0a1f14 30%, #0c2617 60%, #071e12 100%)', fontFamily: "'Open Sans', sans-serif" },
  header:           { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 24px', borderBottom: '1px solid rgba(255,255,255,0.07)' },
  brand:            { fontFamily: "'Raleway', sans-serif", fontSize: '16px', fontWeight: '800', color: '#4a8f82', letterSpacing: '0.5px' },
  welcomeText:      { fontFamily: "'Raleway', sans-serif", fontSize: '14px', fontWeight: '600', color: 'rgba(255,255,255,0.6)' },
  logoutBtn:        { background: 'none', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '6px', color: 'rgba(255,255,255,0.5)', fontSize: '12px', padding: '8px 16px', cursor: 'pointer', minHeight: '44px', fontFamily: "'Open Sans', sans-serif" },
  body:             { maxWidth: '640px', margin: '0 auto', padding: '28px 20px 48px' },
  errorBox:         { background: 'rgba(192,57,43,0.2)', border: '1px solid rgba(192,57,43,0.4)', borderRadius: '8px', padding: '14px 16px', color: '#e74c3c', fontSize: '13px', marginBottom: '20px' },
  loadingBox:       { color: 'rgba(255,255,255,0.4)', fontSize: '14px', textAlign: 'center', padding: '40px 0' },
  earningsStrip:    { display: 'flex', background: 'rgba(74,143,130,0.1)', border: '1px solid rgba(74,143,130,0.25)', borderRadius: '12px', padding: '20px 24px', marginBottom: '32px', alignItems: 'center' },
  earningStat:      { textAlign: 'center', flex: 1 },
  earningNum:       { fontFamily: "'Raleway', sans-serif", fontSize: '22px', fontWeight: '800', color: '#fff', marginBottom: '4px' },
  earningLabel:     { fontSize: '9px', fontWeight: '700', letterSpacing: '1.5px', color: 'rgba(255,255,255,0.35)', fontFamily: "'Raleway', sans-serif" },
  earningDivider:   { width: '1px', height: '40px', background: 'rgba(255,255,255,0.1)', flexShrink: 0, margin: '0 8px' },
  section:          { marginBottom: '32px' },
  sectionLabel:     { fontSize: '10px', fontWeight: '700', letterSpacing: '1.5px', color: 'rgba(255,255,255,0.35)', fontFamily: "'Raleway', sans-serif", marginBottom: '12px' },
  upcomingCard:     { background: 'rgba(74,143,130,0.12)', border: '1px solid rgba(74,143,130,0.3)', borderRadius: '12px', padding: '18px 20px', marginBottom: '10px' },
  upcomingPatient:  { fontSize: '16px', fontWeight: '700', color: '#fff', marginBottom: '5px' },
  upcomingMeta:     { fontSize: '13px', color: 'rgba(255,255,255,0.55)', marginBottom: '12px' },
  emptyCard:        { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '10px', padding: '20px', color: 'rgba(255,255,255,0.3)', fontSize: '14px' },
  badge:            { display: 'inline-block', padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '700', color: '#fff', letterSpacing: '0.5px' },
  historyList:      { borderRadius: '12px', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.07)' },
  historyItem:      { borderBottom: '1px solid rgba(255,255,255,0.05)', background: 'rgba(255,255,255,0.02)' },
  historyRow:       { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '14px 16px' },
  historyLeft:      { flex: 1, paddingRight: '12px' },
  historyDate:      { fontSize: '13px', fontWeight: '600', color: '#fff', marginBottom: '3px' },
  historyDetail:    { fontSize: '12px', color: 'rgba(255,255,255,0.45)', marginBottom: '3px' },
  historyCondition: { fontSize: '11px', color: 'rgba(255,255,255,0.3)', fontStyle: 'italic' },
  receiptToggle:    { background: 'none', border: 'none', color: 'rgba(74,143,130,0.8)', fontSize: '11px', fontWeight: '600', padding: '6px 0 0', cursor: 'pointer', fontFamily: "'Open Sans', sans-serif", letterSpacing: '0.3px' },
  receiptPanel:     { padding: '12px 16px 14px', borderTop: '1px solid rgba(255,255,255,0.07)', background: 'rgba(255,255,255,0.03)' },
  receiptTitle:     { fontSize: '9px', fontWeight: '700', letterSpacing: '1.8px', color: 'rgba(255,255,255,0.3)', fontFamily: "'Raleway', sans-serif", marginBottom: '10px' },
  receiptRow:       { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '7px' },
  receiptLabel:     { fontSize: '11px', color: 'rgba(255,255,255,0.4)' },
  receiptValue:     { fontSize: '12px', fontWeight: '600', color: 'rgba(255,255,255,0.75)' },
  privacyFooter:    { textAlign: 'center', paddingTop: '24px' },
  privacyLink:      { fontSize: '12px', color: 'rgba(255,255,255,0.3)', textDecoration: 'none',
                      fontFamily: "'Open Sans', sans-serif" },
};

export default SoloHealerDashboard;
